/** The core widgets (Home spec 2026-09-23 D2, §3.2): the same entry shape a
 * module uses. Their ids are reserved in the manifest validation.
 *
 * F6 (review ruling): no literal "Quotes" in a core title, blurb or label —
 * the record noun is a per-deployment word (SA-385). `CORE_HOME_WIDGETS`
 * stays a static array (so `CORE_HOME_WIDGET_IDS` is static too, for the
 * manifest guard) with the DEFAULT vocabulary's noun ("Records") baked into
 * its fallback copy; `useCoreHomeWidgets()` is what the page actually
 * renders from — it re-words the two quote-shaped entries (and completes
 * the `new-enquiries` and `closed-this-month` blurbs) with the live `useRecordNoun()`, matching exactly
 * what `QuotesWithCustomersWidget` / `OpenQuotesWidget` print in their own
 * `WidgetHead`. */
import { lazy, useMemo } from 'react'
import { DEFAULT_VOCABULARY } from '@/stores/featureStore'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import type { HomeWidgetEntry } from '@/components/custom/homeWidgetTypes'

const { singular: DEFAULT_SINGULAR, plural: DEFAULT_PLURAL } = DEFAULT_VOCABULARY.record

export const CORE_HOME_WIDGETS: HomeWidgetEntry[] = [
  { id: 'my-work', title: 'My work', feature: 'process_engine', permission: 'tasks:read', width: { default: 2 },
    group: 'work',
    blurb: 'Your open tasks by when they are due; the seat’s, and everyone’s, one tab over.',
    minWidth: 20,
    Component: lazy(() => import('./widgets/MyWorkWidget')),
    Headline: lazy(() => import('./widgets/MyWorkWidget').then((m) => ({ default: m.MyWorkHeadline }))) },
  { id: 'new-enquiries', title: 'New enquiries', feature: 'process_engine', permission: 'inbox:read', width: { default: 1 },
    group: 'customers',
    blurb: `${DEFAULT_PLURAL} received and not yet started.`,
    minWidth: 13.75,
    Component: lazy(() => import('./widgets/NewEnquiriesWidget')),
    Headline: lazy(() => import('./widgets/NewEnquiriesWidget').then((m) => ({ default: m.NewEnquiriesHeadline }))) },
  { id: 'with-customers', title: `${DEFAULT_PLURAL} with customers`, feature: 'workflow_engine', permission: 'inbox:read', width: { default: 2 },
    group: 'customers',
    blurb: `${DEFAULT_PLURAL} out with a customer, the validity running out soonest first, and counters waiting on a decision.`,
    minWidth: 22,
    Component: lazy(() => import('./widgets/QuotesWithCustomersWidget')),
    Headline: lazy(() => import('./widgets/QuotesWithCustomersWidget').then((m) => ({ default: m.QuotesWithCustomersHeadline }))) },
  { id: 'open-quotes', title: `Open ${DEFAULT_PLURAL.toLowerCase()}`, feature: 'workflow_engine', permission: 'inbox:read', width: { default: 1 },
    group: 'customers',
    blurb: `Every open ${DEFAULT_SINGULAR.toLowerCase()}, by its commercial status.`,
    minWidth: 15,
    Component: lazy(() => import('./widgets/OpenQuotesWidget')),
    Headline: lazy(() => import('./widgets/OpenQuotesWidget').then((m) => ({ default: m.OpenQuotesHeadline }))) },
  {
    // SA-472 (wireframe decision 50) offered it in Add only; since SA-1178 it
    // closes the default layout (design pass spec §3.1, row 11).
    id: 'closed-this-month', title: 'Closed this month', feature: 'workflow_engine', permission: 'inbox:read', width: { default: 1 },
    group: 'business',
    blurb: `${DEFAULT_PLURAL} closed this month: won, and not won.`,
    minWidth: 12.5,
    Component: lazy(() => import('./widgets/ClosedThisMonthWidget')),
    Headline: lazy(() => import('./widgets/ClosedThisMonthWidget').then((m) => ({ default: m.ClosedThisMonthHeadline }))) },
  {
    // SA-1171 (spec §3.1, D1): the tiles row, second on the default page since
    // SA-1178 (it was a candidate until the new default layout landed).
    id: 'at-a-glance', title: 'At a glance', feature: 'home_dashboard', permission: 'inbox:read', width: { default: 3, allowed: [2, 3] },
    group: 'business',
    blurb: 'The numbers that matter today: what is out with customers, new enquiries, overdue tasks, and the fleet.',
    minWidth: 16.25,
    Component: lazy(() => import('./widgets/AtAGlanceWidget')),
    Headline: lazy(() => import('./widgets/AtAGlanceWidget').then((m) => ({ default: m.AtAGlanceHeadline }))) },
]
export const CORE_HOME_WIDGET_IDS = CORE_HOME_WIDGETS.map((w) => w.id)

/** `CORE_HOME_WIDGETS` worded with the deployment's own record noun (the
 * page's read; `homeLayout`/`validateManifest` use the static array). */
export function useCoreHomeWidgets(): HomeWidgetEntry[] {
  const { singular, plural } = useRecordNoun()
  return useMemo(() => CORE_HOME_WIDGETS.map((w) => {
    switch (w.id) {
      case 'new-enquiries':
        return { ...w, blurb: `${plural} received and not yet started.` }
      case 'with-customers':
        return { ...w, title: `${plural} with customers`,
          blurb: `${plural} out with a customer, the validity running out soonest first, and counters waiting on a decision.` }
      case 'open-quotes':
        return { ...w, title: `Open ${plural.toLowerCase()}`,
          blurb: `Every open ${singular.toLowerCase()}, by its commercial status.` }
      case 'closed-this-month':
        return { ...w, blurb: `${plural} closed this month: won, and not won.` }
      default:
        return w
    }
  }), [singular, plural])
}
